<script lang="ts">
  import { fly } from "svelte/transition";
  import { SECS_PER_PAGE, type ShareTargetDto } from "@piano/shared";
  import { ApiError } from "../lib/api/client.js";
  import { api } from "../lib/api/index.js";
  import { formatBytes, formatRelative, plural } from "../lib/format.js";
  import { router } from "../lib/router.svelte.js";
  import { library } from "../lib/stores/library.svelte.js";
  import { playlists } from "../lib/stores/playlists.svelte.js";
  import { session } from "../lib/stores/session.svelte.js";
  import { ui } from "../lib/stores/ui.svelte.js";
  import Cover from "./Cover.svelte";
  import Icon from "./Icon.svelte";
  import Sheet from "./Sheet.svelte";

  let { documentId }: { documentId: string } = $props();

  type View = "main" | "setlists" | "categories" | "info" | "share";
  let view = $state<View>("main");
  let dir = $state(1);
  const piece = $derived(library.byId.get(documentId));
  const isOwner = $derived(piece?.accessRole === "owner");
  const canDelete = $derived(isOwner || session.isAdmin);

  function close(): void {
    ui.actionsFor = null;
  }
  function show(next: View): void {
    dir = next === "main" ? -1 : 1;
    view = next;
  }

  // --- Setlists ---
  const inLists = $derived(new Set(playlists.containing(documentId).map((p) => p.id)));
  let newListName = $state("");
  async function toggleList(id: string): Promise<void> {
    const ids = new Set(inLists);
    if (ids.has(id)) ids.delete(id);
    else ids.add(id);
    try {
      await api.setDocumentPlaylists(documentId, [...ids]);
      await playlists.load();
    } catch (error) {
      ui.error(error);
    }
  }
  async function createList(event: SubmitEvent): Promise<void> {
    event.preventDefault();
    const name = newListName.trim();
    if (!name) return;
    newListName = "";
    await playlists.create(name, [documentId]);
  }

  // --- Kategorien ---
  let newCategory = $state("");
  function toggleCategory(id: string): void {
    if (!piece) return;
    const ids = piece.categoryIds.includes(id)
      ? piece.categoryIds.filter((c) => c !== id)
      : [...piece.categoryIds, id];
    void library.setCategories(documentId, ids);
  }
  async function createCategory(event: SubmitEvent): Promise<void> {
    event.preventDefault();
    const name = newCategory.trim();
    if (!name || !piece) return;
    newCategory = "";
    try {
      const category = await library.createCategory({ name });
      await library.setCategories(documentId, [...piece.categoryIds, category.id]);
    } catch (error) {
      ui.error(error);
    }
  }

  // --- Tempo ---
  let secs = $state(0);
  $effect(() => {
    if (view === "info" && piece) secs = piece.secsPerPage ?? SECS_PER_PAGE.default;
  });
  async function saveTempo(): Promise<void> {
    const value = Math.round(Math.min(SECS_PER_PAGE.max, Math.max(SECS_PER_PAGE.min, Number(secs))));
    try {
      await api.patchPrefs(documentId, { secsPerPage: value });
      library.setSecsPerPage(documentId, value);
      ui.toast("Tempo gespeichert", "success");
      show("main");
    } catch (error) {
      ui.error(error);
    }
  }

  // --- Teilen ---
  let targets = $state<ShareTargetDto[]>([]);
  let sharedWith = $state<Set<string>>(new Set());
  let shareLoading = $state(false);
  async function openShare(): Promise<void> {
    show("share");
    shareLoading = true;
    try {
      const [all, current] = await Promise.all([api.shareTargets(), api.shares(documentId)]);
      targets = all;
      sharedWith = new Set(current.sharedWith.map((t) => t.id));
    } catch (error) {
      ui.error(error);
    } finally {
      shareLoading = false;
    }
  }
  async function toggleShare(id: string): Promise<void> {
    const next = new Set(sharedWith);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    try {
      const result = await api.setShares(documentId, [...next]);
      sharedWith = new Set(result.sharedWith.map((t) => t.id));
    } catch (error) {
      ui.error(error);
    }
  }

  // --- Entfernen/Löschen ---
  async function leave(): Promise<void> {
    if (!piece) return;
    const ok = await ui.confirm({
      title: "Aus deiner Bibliothek entfernen?",
      message: `„${piece.name}“ wurde von ${piece.owner.displayName} geteilt. Es verschwindet nur bei dir.`,
      confirmLabel: "Entfernen",
      danger: true,
    });
    if (!ok) return;
    try {
      await library.leave(documentId);
      close();
      ui.toast("Entfernt", "success");
    } catch (error) {
      ui.error(error);
    }
  }
  async function remove(): Promise<void> {
    if (!piece) return;
    const ok = await ui.confirm({
      title: "Noten löschen?",
      message: `„${piece.name}“ wird für alle gelöscht und kommt 30 Tage in den Papierkorb des Servers.`,
      confirmLabel: "Löschen",
      danger: true,
    });
    if (!ok) return;
    try {
      await library.remove(documentId);
      close();
      ui.toast("Gelöscht", "success");
    } catch (error) {
      if (error instanceof ApiError && error.code === "DOCUMENT_SHARED") {
        const count = (error.details as { shareCount?: number })?.shareCount ?? 0;
        const force = await ui.confirm({
          title: "Das Stück ist geteilt",
          message: `Es ist mit ${plural(count, "Person", "Personen")} geteilt und verschwindet auch dort. Trotzdem löschen?`,
          confirmLabel: "Trotzdem löschen",
          danger: true,
        });
        if (!force) return;
        await library.remove(documentId, true).catch((err: unknown) => ui.error(err));
        close();
        return;
      }
      ui.error(error);
    }
  }
</script>

{#if piece}
  <Sheet title="Aktionen für {piece.name}" onclose={close} width={440}>
    <div class="head">
      <span class="cover"><Cover src={piece.thumb} ratio="1" radius="8px" /></span>
      <span class="text">
        <span class="title">{piece.name}</span>
        <span class="t3 sub">{piece.artist || piece.fileName}</span>
      </span>
      {#if view !== "main"}
        <button type="button" class="icon-btn" aria-label="Zurück" onclick={() => show("main")}
          ><Icon name="back" /></button
        >
      {/if}
    </div>

    {#key view}
      <div class="view" in:fly={{ x: 40 * dir, duration: 280 }}>
        {#if view === "main"}
          <button type="button" class="item" onclick={() => (router.go(`/noten/${documentId}`), close())}>
            <Icon name="play" /> Öffnen
          </button>
          {#if playlists.active}
            <button
              type="button"
              class="item"
              onclick={() => playlists.active && playlists.add(playlists.active.id, documentId)}
            >
              <Icon name="plus" /> Zu „{playlists.active.name}“
            </button>
          {/if}
          <button type="button" class="item" onclick={() => show("setlists")}>
            <Icon name="setlist" /> In Setlists …
            <span class="hint">{inLists.size ? plural(inLists.size, "Setlist", "Setlists") : ""}</span>
          </button>
          <button type="button" class="item" onclick={() => library.toggleFavorite(documentId)}>
            <span class:fav={piece.favorite}><Icon name={piece.favorite ? "starFill" : "star"} /></span>
            {piece.favorite ? "Aus Favoriten entfernen" : "Zu Favoriten"}
          </button>
          <button type="button" class="item" onclick={() => show("categories")}>
            <Icon name="tag" /> Kategorien
            <span class="hint"
              >{piece.categoryIds
                .map((id) => library.categoryById.get(id)?.name)
                .filter(Boolean)
                .join(", ")}</span
            >
          </button>
          <button type="button" class="item" onclick={() => show("info")}>
            <Icon name="info" /> Infos &amp; Tempo
          </button>
          {#if isOwner}
            <button type="button" class="item" onclick={openShare}>
              <Icon name="share" /> Teilen
              <span class="hint">{piece.shareCount ? `mit ${piece.shareCount}` : ""}</span>
            </button>
          {/if}
          <div class="sep"></div>
          {#if piece.accessRole === "shared"}
            <button type="button" class="item danger" onclick={leave}
              ><Icon name="leave" /> Aus meiner Bibliothek entfernen</button
            >
          {/if}
          {#if canDelete}
            <button type="button" class="item danger" onclick={remove}><Icon name="trash" /> Löschen</button>
          {/if}
        {:else if view === "setlists"}
          {#each playlists.list as playlist (playlist.id)}
            <button
              type="button"
              class="item"
              role="menuitemcheckbox"
              aria-checked={inLists.has(playlist.id)}
              onclick={() => toggleList(playlist.id)}
            >
              <span class="swatch" style:background={playlist.accentColor}>{playlist.icon}</span>
              <span class="grow ellipsis">{playlist.name}</span>
              <span class="check" class:on={inLists.has(playlist.id)}
                ><Icon name="check" size={16} stroke={3} /></span
              >
            </button>
          {/each}
          <form class="inline" onsubmit={createList}>
            <input class="input" placeholder="Neue Setlist …" bind:value={newListName} maxlength="120" />
            <button class="btn btn-primary" type="submit" disabled={!newListName.trim()}>Anlegen</button>
          </form>
        {:else if view === "categories"}
          {#each library.categories as category (category.id)}
            <button
              type="button"
              class="item"
              role="menuitemcheckbox"
              aria-checked={piece.categoryIds.includes(category.id)}
              onclick={() => toggleCategory(category.id)}
            >
              <span class="swatch" style:background={category.color}>{category.icon}</span>
              <span class="grow ellipsis">{category.name}</span>
              <span class="check" class:on={piece.categoryIds.includes(category.id)}
                ><Icon name="check" size={16} stroke={3} /></span
              >
            </button>
          {/each}
          <form class="inline" onsubmit={createCategory}>
            <input class="input" placeholder="Neue Kategorie …" bind:value={newCategory} maxlength="120" />
            <button class="btn btn-primary" type="submit" disabled={!newCategory.trim()}>Anlegen</button>
          </form>
        {:else if view === "info"}
          <dl class="facts">
            <dt>Seiten</dt>
            <dd>{piece.pageCount ?? "–"}</dd>
            <dt>Größe</dt>
            <dd>{formatBytes(piece.sizeBytes)}</dd>
            <dt>Hinzugefügt</dt>
            <dd>{formatRelative(piece.createdAt)}</dd>
            <dt>Zuletzt gespielt</dt>
            <dd>{formatRelative(piece.lastOpenedAt)}</dd>
            <dt>Besitzer</dt>
            <dd>{piece.owner.displayName}</dd>
            <dt>Datei</dt>
            <dd class="file">{piece.fileName}</dd>
          </dl>
          <label class="field tempo">
            Auto-Scroll-Tempo (Sekunden pro Seite)
            <span class="tempo-row">
              <input
                class="input"
                type="number"
                min={SECS_PER_PAGE.min}
                max={SECS_PER_PAGE.max}
                bind:value={secs}
              />
              <button type="button" class="btn btn-primary" onclick={saveTempo}>Speichern</button>
            </span>
          </label>
        {:else if view === "share"}
          {#if shareLoading}
            <p class="t2 pad">Lädt …</p>
          {:else if targets.length === 0}
            <p class="t2 pad">Es gibt noch keine anderen aktiven Konten zum Teilen.</p>
          {:else}
            {#each targets as target (target.id)}
              <button
                type="button"
                class="item"
                role="menuitemcheckbox"
                aria-checked={sharedWith.has(target.id)}
                onclick={() => toggleShare(target.id)}
              >
                <span class="avatar">{target.displayName.charAt(0).toUpperCase()}</span>
                <span class="grow ellipsis">{target.displayName}</span>
                <span class="check" class:on={sharedWith.has(target.id)}
                  ><Icon name="check" size={16} stroke={3} /></span
                >
              </button>
            {/each}
          {/if}
        {/if}
      </div>
    {/key}
  </Sheet>
{/if}

<style>
  .head {
    display: flex;
    align-items: center;
    gap: 14px;
    padding: 0 8px 14px;
    border-bottom: 1px solid var(--line);
    margin-bottom: 6px;
  }
  .cover {
    width: 56px;
    flex: 0 0 56px;
  }
  .text {
    display: flex;
    flex-direction: column;
    gap: 2px;
    flex: 1;
    min-width: 0;
  }
  .title {
    font-weight: 800;
    font-size: 16px;
  }
  .sub {
    font-size: 13px;
    font-weight: 600;
  }
  .view {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }
  .item {
    display: flex;
    align-items: center;
    gap: 14px;
    min-height: 52px;
    padding: 0 12px;
    border-radius: var(--r-md);
    font-weight: 600;
    font-size: 15px;
    text-align: left;
    color: var(--text);
    transition: background var(--dur-tap);
  }
  .item :global(svg) {
    color: var(--text-2);
    flex: 0 0 auto;
  }
  .item:hover,
  .item:active {
    background: rgba(255, 255, 255, 0.07);
  }
  .item.danger,
  .item.danger :global(svg) {
    color: var(--danger);
  }
  .fav :global(svg) {
    color: var(--accent-2);
  }
  .hint {
    margin-left: auto;
    font-size: 12px;
    color: var(--text-3);
    max-width: 45%;
    text-align: right;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .sep {
    height: 1px;
    background: var(--line);
    margin: 6px 0;
  }
  .swatch,
  .avatar {
    width: 34px;
    height: 34px;
    flex: 0 0 34px;
    border-radius: var(--r-sm);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 16px;
  }
  .avatar {
    border-radius: 50%;
    background: var(--raised-2);
    font-weight: 800;
  }
  .grow {
    flex: 1;
    min-width: 0;
  }
  .check {
    width: 26px;
    height: 26px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: inset 0 0 0 2px var(--line-strong);
    color: transparent;
    transition:
      background var(--dur-fast),
      transform var(--dur-base) var(--ease-pop);
  }
  .check.on {
    background: var(--accent);
    box-shadow: none;
    transform: scale(1.08);
  }
  .check.on :global(svg) {
    color: var(--on-accent);
  }
  .inline {
    display: flex;
    gap: 8px;
    padding: 10px 4px 4px;
  }
  .inline .input {
    flex: 1;
    min-width: 0;
  }
  .facts {
    display: grid;
    grid-template-columns: auto 1fr;
    gap: 8px 18px;
    margin: 6px 12px 14px;
    font-size: 14px;
  }
  dt {
    color: var(--text-3);
    font-weight: 700;
  }
  dd {
    margin: 0;
    font-weight: 600;
  }
  .file {
    word-break: break-all;
  }
  .tempo {
    padding: 0 12px 8px;
  }
  .tempo-row {
    display: flex;
    gap: 8px;
  }
  .tempo-row .input {
    width: 110px;
  }
  .pad {
    padding: 12px;
  }
</style>
